// src/components/VehicleDashboard.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  doc, getDoc, collection, query, where, onSnapshot, 
  orderBy, getDocs, writeBatch, updateDoc, Timestamp,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import * as XLSX from 'xlsx'; 
import ContentCopyIcon from '@mui/icons-material/ContentCopy'; 
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { format } from 'date-fns'; 
import { useTranslation } from 'react-i18next';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { pdf } from '@react-pdf/renderer';
import SetupPdf from './SetupPdf';
import VehicleHistoryPdf from './VehicleHistoryPdf'; // Aggiunto import

import QRCode from "react-qr-code";
import QrCodeIcon from '@mui/icons-material/QrCode';

import {
  Box,
  Typography, 
  Button, List, ListItem, ListItemButton,
  ListItemText, CircularProgress, 
  IconButton, Checkbox,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Tooltip, 
  ListItemIcon, 
  Divider, 
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import MuiTab from '@mui/material/Tab';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit'; 
import ArticleIcon from '@mui/icons-material/Article'; 
import ListAltIcon from '@mui/icons-material/ListAlt'; 
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HistoryIcon from '@mui/icons-material/History'; 
import MicIcon from '@mui/icons-material/Mic';
import IssueLog from './IssueLog'; 
import ComponentLifing from './ComponentLifing';


// --- FUNZIONE HELPER ---
const getVal = (data, path, unit = '') => {
    const parts = path.split('.');
    let current = data;
    for (const part of parts) {
        if (current === null || current === undefined) return '';
        current = current[part];
    }
    return (current !== null && current !== undefined ? current : '') + (unit ? ` ${unit}` : '');
};

const formatDateForExcel = (timestamp) => {
  if (!timestamp) return '';
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  } catch (e) {
    return '';
  }
};
// --- FINE HELPER ---


function VehicleDashboard({ teamId, userRole }) {
  const { t, i18n } = useTranslation('vehicle');
  const currentLang = i18n.language;

  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const eventParam = searchParams.get('event');
  
  // Stati
  const [vehicle, setVehicle] = useState(null);
  const [isLoadingVehicle, setIsLoadingVehicle] = useState(true);
  const [setupSheets, setSetupSheets] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [isLoadingSetups, setIsLoadingSetups] = useState(true);
  const [isLoadingFeedbacks, setIsLoadingFeedbacks] = useState(true);
  const [selectedSetups, setSelectedSetups] = useState([]);
  const isManager = userRole === 'admin' || userRole === 'Engineer';
  const canManageSetup = isManager || userRole === 'Meccanico'; 
  const [activeTab, setActiveTab] = useState('1'); 
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingVehicle, setIsSavingVehicle] = useState(false);
  const [driversList, setDriversList] = useState([]); 
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(true);
  const [templatesList, setTemplatesList] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  
  const [editFormData, setEditFormData] = useState({
    marca: '', 
    modello: '', 
    numeroGara: '', 
    dryWeight: '', 
    mainDriver: null, 
    passportLink: '', 
    template: null 
  });

  const [eventChecklists, setEventChecklists] = useState([]);
  const [isLoadingEventChecklists, setIsLoadingEventChecklists] = useState(true);
  const [allFutureEvents, setAllFutureEvents] = useState([]);
  const [isLoadingFutureEvents, setIsLoadingFutureEvents] = useState(true);
  const eventSectionRefs = useRef({}); 
  const [isScrolling, setIsScrolling] = useState(false); 

  const [isExportingHistory, setIsExportingHistory] = useState(false);
  
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);

  
  const handleChangeTab = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue !== '4' && eventParam) {
      navigate(`/vehicle/${vehicleId}`);
    }
  };
  
  // useEffects (Invariati)
  useEffect(() => {
    if (!teamId || !vehicleId) { setIsLoadingVehicle(false); return; }
    setIsLoadingVehicle(true);
    const vehicleDocRef = doc(db, 'vehicles', vehicleId);
    const unsubscribe = onSnapshot(vehicleDocRef, (vehicleSnap) => {
      if (vehicleSnap.exists() && vehicleSnap.data().teamId === teamId) {
        setVehicle(vehicleSnap.data());
      } else { setVehicle(null); }
      setIsLoadingVehicle(false);
    }, (error) => {
      console.error("Error fetching vehicle with onSnapshot:", error);
      setVehicle(null);
      setIsLoadingVehicle(false);
    });
    return () => unsubscribe();
  }, [vehicleId, teamId]);

  useEffect(() => {
    if (!teamId || !vehicleId) {
      setIsLoadingSetups(false);
      return;
    }
    setIsLoadingSetups(true);
    const setupsQuery = query(
      collection(db, "setupSheets"),
      where("vehicleId", "==", vehicleId),
      where("teamId", "==", teamId),
      orderBy("createdAt", "desc")
    );
    const unsubscribe = onSnapshot(setupsQuery, (querySnapshot) => {
      setSetupSheets(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setIsLoadingSetups(false);
    }, (error) => {
      console.error("VehicleDashboard - Setup listener error:", error);
      setIsLoadingSetups(false);
    });
    return () => { unsubscribe(); };
  }, [vehicleId, teamId]);

  useEffect(() => {
    // Bug fix: anche se teamId è momentaneamente undefined, settare il flag a false
    // altrimenti il render rimane bloccato nello spinner globale (gating linea ~700).
    if (!teamId) { setIsLoadingDrivers(false); return; }
    setIsLoadingDrivers(true);
    // Timeout di sicurezza: dopo 10s sblocca lo spinner anche se Firestore non risponde.
    const timeout = setTimeout(() => {
      console.warn("Drivers query timeout — sblocco UI dopo 10s");
      setIsLoadingDrivers(false);
    }, 10000);
    const q = query(collection(db, "drivers"), where("teamId", "==", teamId), orderBy("nomeCompleto", "asc"));
    const unsub = onSnapshot(q, (qs) => {
      clearTimeout(timeout);
      setDriversList(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoadingDrivers(false);
    }, (err) => {
      clearTimeout(timeout);
      console.error("Error loading drivers:", err);
      setIsLoadingDrivers(false);
    });
    return () => { clearTimeout(timeout); unsub(); };
  }, [teamId]);

  useEffect(() => {
    if (!teamId) { setIsLoadingTemplates(false); return; }
    setIsLoadingTemplates(true);
    const timeout = setTimeout(() => {
      console.warn("Templates query timeout — sblocco UI dopo 10s");
      setIsLoadingTemplates(false);
    }, 10000);
    const q = query(
      collection(db, "setupTemplates"),
      where("teamId", "==", teamId),
      orderBy("templateName", "asc")
    );
    const unsub = onSnapshot(q, (qs) => {
      clearTimeout(timeout);
      setTemplatesList(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoadingTemplates(false);
    }, (err) => {
      clearTimeout(timeout);
      console.error("Error loading templates:", err);
      setIsLoadingTemplates(false);
    });
    return () => { clearTimeout(timeout); unsub(); };
  }, [teamId]);

  useEffect(() => {
    if (!teamId || !vehicleId) {
      setIsLoadingFeedbacks(false);
      return;
    }
    setIsLoadingFeedbacks(true);
    const feedbackQuery = query(
      collection(db, "driverFeedback"),
      where("vehicleId", "==", vehicleId),
      where("teamId", "==", teamId)
    );
    const unsubFeedbacks = onSnapshot(feedbackQuery, (qs) => {
      // sort by date descending
      const fbs = qs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      fbs.sort((a, b) => b.createdAt?.toMillis() - a.createdAt?.toMillis());
      setFeedbacks(fbs);
      setIsLoadingFeedbacks(false);
    }, (err) => {
      console.error("Error loading feedbacks:", err);
      setIsLoadingFeedbacks(false);
    });
    return () => unsubFeedbacks();
  }, [teamId, vehicleId]);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeTab !== '4' || !teamId || !vehicleId) {
      setEventChecklists([]);
      setAllFutureEvents([]);
      setIsLoadingEventChecklists(false);
      setIsLoadingFutureEvents(false);
      return;
    }
    setIsLoadingEventChecklists(true);
    setIsLoadingFutureEvents(true);

    const qChecklists = query(
      collection(db, "eventChecklists"),
      where("teamId", "==", teamId),
      where("vehicleId", "==", vehicleId)
    );
    const unsubChecklists = onSnapshot(qChecklists, (snapshot) => {
      const checklists = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      checklists.sort((a, b) => a.templateName.localeCompare(b.templateName));
      setEventChecklists(checklists);
      setIsLoadingEventChecklists(false);
    }, (error) => {
      console.error("Error loading event checklists:", error);
      setIsLoadingEventChecklists(false);
    });

    const now = Timestamp.now();
    const qEvents = query(
      collection(db, "events"),
      where("teamId", "==", teamId),
      where("endDate", ">", now),
      orderBy("endDate", "asc")
    );
    const unsubEvents = onSnapshot(qEvents, (snapshot) => {
      const eventsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllFutureEvents(eventsList);
      setIsLoadingFutureEvents(false);
    }, (error) => {
      console.error("Error loading future events:", error);
      setIsLoadingFutureEvents(false);
    });

    return () => {
      unsubChecklists();
      unsubEvents();
    };
  }, [activeTab, teamId, vehicleId]); 

  useEffect(() => {
    if (isLoadingEventChecklists || isLoadingFutureEvents || !eventParam || isScrolling) {
      return;
    }
    const element = eventSectionRefs.current[eventParam];
    if (element) {
      setIsScrolling(true); 
      setTimeout(() => {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        setTimeout(() => setIsScrolling(false), 1000); 
      }, 300); 
    }
  }, [eventParam, isLoadingEventChecklists, isLoadingFutureEvents, isScrolling]);


  // --- Funzioni Handler ---
  const handleSelectSetup = (e, setupId) => {
    e.stopPropagation();
    if (!isManager) {
        alert(t('setups.compare_error_permission')); 
        return;
    }
    if (selectedSetups.includes(setupId)) {
      setSelectedSetups(prev => prev.filter(id => id !== setupId));
    } else {
      if (selectedSetups.length < 2) {
        setSelectedSetups(prev => [...prev, setupId]);
      } else {
        e.preventDefault();
        alert(t('setups.compare_error_limit')); 
      }
    }
  };

  const handleDeleteVehicle = async () => {
    if (!vehicle) return;
    const vehicleName = `${vehicle.marca} ${vehicle.modello}`;
    if (window.confirm(t('delete_confirm', { name: vehicleName }))) { 
      try {
        const setupsQuery = query(collection(db, "setupSheets"), where("vehicleId", "==", vehicleId));
        const setupSnapshots = await getDocs(setupsQuery); 
        const batch = writeBatch(db); 
        setupSnapshots.forEach((doc) => batch.delete(doc.ref));
        batch.delete(doc(db, 'vehicles', vehicleId));
        await batch.commit();
        navigate('/'); 
      } catch (error) { 
        console.error("Error deleting vehicle:", error); 
        alert(t('delete_error')); 
      }
    }
  };
  
  const handleDuplicateSetup = (setupId) => {
    if (!canManageSetup) return; 
    navigate(`/vehicle/${vehicleId}/setup/new`, { 
        state: { duplicateFromId: setupId } 
    });
  };

  const handleDeleteSetup = async (setupId, sessionName) => {
    if (!canManageSetup) return; 
    const setupDisplayName = sessionName || t('setups.unnamed_setup');
    if (window.confirm(t('setups.delete_confirm', { name: setupDisplayName }))) {
      try {
        const setupRef = doc(db, 'setupSheets', setupId);
        await deleteDoc(setupRef);
      } catch (error) {
        console.error("Errore durante l'eliminazione del setup:", error);
        alert(t('setups.delete_error')); 
      }
    }
  };
  
  const getLocaleFormat = () => (currentLang === 'it' ? 'it-IT' : 'en-GB');

  const handleExportSetup = async (setupId, sessionName) => {
      if (!canManageSetup) return; 
      try {
          const setupRef = doc(db, 'setupSheets', setupId);
          const snap = await getDoc(setupRef); 
          if (!snap.exists() || snap.data().teamId !== teamId) {
              alert(t('setups.export_error')); 
              return;
          }
          const setup = snap.data();
          const data = setup.setupData || {};
          const displayDate = setup.dataSessione ? new Date(setup.dataSessione.toDate()).toLocaleDateString(getLocaleFormat()) : 'N/A';
          
          const dryWeight = Number(getVal(data, 'weights.dryWeight', 0));
          const driverWeight = Number(getVal(data, 'weights.driverWeight', 0));
          const fuelLiters = Number(getVal(data, 'weights.fuelLiters', 0));
          const minWeight = Number(getVal(data, 'weights.minRegWeight', 0));
          const totalCalculatedWeight = dryWeight + driverWeight + (fuelLiters * 0.75);
          const calculatedBallastValue = minWeight > 0 ? (minWeight - totalCalculatedWeight).toFixed(1) : 'N/A';
          
          const dataPerExcelComplete = [
            ["SETUP SHEET"], [], ["SESSION INFORMATION"],
            ["Event", setup.eventName || 'N/A'], ["Track", setup.trackName || 'N/A'],
            ["Session", setup.sessionName || 'N/A'], ["Date", displayDate],
            ["Driver", setup.nomePilota || 'N/A'], ["Air Temp", getVal(data, 'meteo.tempAria', '°C')],
            ["Track Temp", getVal(data, 'meteo.tempAsfalto', '°C')], ["Humidity", getVal(data, 'meteo.umidita', '%')],
            [], ["WEIGHTS AND BALANCE"],
            ["Min. Reg. Weight", getVal(data, 'weights.minRegWeight', 'kg')],
            ["Driver Weight", getVal(data, 'weights.driverWeight', 'kg')],
            ["Dry Weight (Vehicle)", getVal(data, 'weights.dryWeight', 'kg')],
            ["Fuel Liters", getVal(data, 'weights.fuelLiters', 'L')],
            ["Calculated Ballast", calculatedBallastValue || 'N/A'],
            ["Applied Ballast", getVal(data, 'weights.ballast', 'kg')],
            ["Total Measured Weight", getVal(data, 'weights.totalWeight', 'kg')],
            ["Total Front Weight", getVal(data, 'weights.totalFrontWeight', 'kg')],
            ["Total Rear Weight", getVal(data, 'weights.totalRearWeight', 'kg')],
            ["Weight Dist.", getVal(data, 'weights.weightRip', '%')],
            [], ["CORNER WEIGHTS (kg)"],
            ["Front Left (FL)", getVal(data, 'weights.cornerFL')],
            ["Front Right (FR)", getVal(data, 'weights.cornerFR')],
            ["Rear Left (RL)", getVal(data, 'weights.cornerRL')],
            ["Rear Right (RR)", getVal(data, 'weights.cornerRR')],
            [], ["FRONT AXLE"],
            ["Toe FL", getVal(data, 'anteriore.convergenzaSX', `mm (${getVal(data, 'anteriore.toeInOrOutSX')})`), "Camber FL", getVal(data, 'anteriore.camberSX', 'deg')],
            ["Toe FR", getVal(data, 'anteriore.convergenzaDX', `mm (${getVal(data, 'anteriore.toeInOrOutDX')})`), "Camber FR", getVal(data, 'anteriore.camberDX', 'deg')],
            ["Caster FL", getVal(data, 'anteriore.casterSX', 'deg'), "Caster FR", getVal(data, 'anteriore.casterDX', 'deg')],
            ["Target H. FL", getVal(data, 'anteriore.altezzaTargetSX', 'mm'), "Target H. FR", getVal(data, 'anteriore.altezzaTargetDX', 'mm')],
            ["Measured H. FL", getVal(data, 'anteriore.altezzaMeasuredSX', 'mm'), "Measured H. FR", getVal(data, 'anteriore.altezzaMeasuredDX', 'mm')],
            ["Spring FL", getVal(data, 'anteriore.mollaSX'), "Spring FR", getVal(data, 'anteriore.mollaDX')],
            ["ARB Pos. Front", getVal(data, 'anteriore.barra')],
            ["Damp. Bump FL", getVal(data, 'anteriore.ammortizzatori.bumpSX'), "Damp. Rebound FL", getVal(data, 'anteriore.ammortizzatori.reboundSX')],
            ["Damp. Bump FR", getVal(data, 'anteriore.ammortizzatori.bumpDX'), "Damp. Rebound FR", getVal(data, 'anteriore.ammortizzatori.reboundDX')],
            [], ["REAR AXLE"],
            ["Toe RL", getVal(data, 'posteriore.convergenzaSX', `mm (${getVal(data, 'posteriore.toeInOrOutSX')})`), "Camber RL", getVal(data, 'posteriore.camberSX', 'deg')],
            ["Toe RR", getVal(data, 'posteriore.convergenzaDX', `mm (${getVal(data, 'posteriore.toeInOrOutDX')})`), "Camber RR", getVal(data, 'posteriore.camberDX', 'deg')],
            ["Target H. RL", getVal(data, 'posteriore.altezzaTargetSX', 'mm'), "Target H. RR", getVal(data, 'posteriore.altezzaTargetDX', 'mm')],
            ["Measured H. RL", getVal(data, 'posteriore.altezzaMeasuredSX', 'mm'), "Measured H. RR", getVal(data, 'posteriore.altezzaMeasuredDX', 'mm')],
            ["Spring RL", getVal(data, 'posteriore.mollaSX'), "Spring RR", getVal(data, 'posteriore.mollaDX')],
            ["ARB Pos. Rear", getVal(data, 'posteriore.barra')],
            ["Damp. Bump RL", getVal(data, 'posteriore.ammortizzatori.bumpSX'), "Damp. Rebound RL", getVal(data, 'posteriore.ammortizzatori.reboundSX')],
            ["Damp. Bump RR", getVal(data, 'posteriore.ammortizzatori.bumpDX'), "Damp. Rebound RR", getVal(data, 'posteriore.ammortizzatori.reboundDX')],
            [], ["TIRES"],
            ["P. Cold FL", getVal(data, 'gomme_ant_sx.pFreddo', 'bar'), "P. Hot FL", getVal(data, 'gomme_ant_sx.pCaldo', 'bar')],
            ["T. Int FL", getVal(data, 'gomme_ant_sx.tInt', '°C'), "T. Mid FL", getVal(data, 'gomme_ant_sx.tMed', '°C'), "T. Ext FL", getVal(data, 'gomme_ant_sx.tExt', '°C')],
            ["P. Cold FR", getVal(data, 'gomme_ant_dx.pFreddo', 'bar'), "P. Hot FR", getVal(data, 'gomme_ant_dx.pCaldo', 'bar')],
            ["T. Int FR", getVal(data, 'gomme_ant_dx.tInt', '°C'), "T. Mid FR", getVal(data, 'gomme_ant_dx.tMed', '°C'), "T. Ext FR", getVal(data, 'gomme_ant_dx.tExt', '°C')],
            ["P. Cold RL", getVal(data, 'gomme_post_sx.pFreddo', 'bar'), "P. Hot RL", getVal(data, 'gomme_post_sx.pCaldo', 'bar')],
            ["T. Int RL", getVal(data, 'gomme_post_sx.tInt', '°C'), "T. Mid RL", getVal(data, 'gomme_post_sx.tMed', '°C'), "T. Ext RL", getVal(data, 'gomme_post_sx.tExt', '°C')],
            ["P. Cold RR", getVal(data, 'gomme_post_dx.pFreddo', 'bar'), "P. Hot RR", getVal(data, 'gomme_post_dx.pCaldo', 'bar')],
            ["T. Int RR", getVal(data, 'gomme_post_dx.tInt', '°C'), "T. Mid RR", getVal(data, 'gomme_post_dx.tMed', '°C'), "T. Ext RR", getVal(data, 'gomme_post_dx.tExt', '°C')],
            [], ["AERO, DIFF. & FUEL"],
            ["Rear Wing Setting", getVal(data, 'aero.alaSetting')],
            ["Rear Wing Angle", getVal(data, 'aero.alaAngle', 'deg')],
            ["Splitter", getVal(data, 'aero.splitter')],
            ["Diff. Pre-Load", getVal(data, 'differenziale.precarico', 'Nm'), "Diff. Lock", getVal(data, 'differenziale.bloccaggio', '%')],
            ["Fuel Consumption", getVal(data, 'differenziale.fuelConsumption', 'L/lap')],
            ["Gear Ratios", getVal(data, 'differenziale.gearRatios')],
            [], ["FEEDBACK & NOTES"],
            ["Best Lap", getVal(data, 'feedback.bestLap')],
            ["Times List", getVal(data, 'feedback.listaTempi')],
            ["Technician Notes", getVal(data, 'feedback.noteTecnico')],
            ["Driver Feedback", getVal(data, 'feedback.feedbackPilota')],
            ["Telemetry Link", getVal(data, 'feedback.linkTelemetria')],
            ["Video Link", getVal(data, 'feedback.linkVideo')],
          ];
          const ws = XLSX.utils.aoa_to_sheet(dataPerExcelComplete);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, `${setup.eventName} Setup`); 
          const filename = `Setup_${sessionName.replace(/\s/g, '_')}_${setupId}.xlsx`;
          XLSX.writeFile(wb, filename);
      } catch (error) {
          console.error("Error exporting setup:", error);
          alert(t('setups.export_alert_error')); 
      }
  };

  const handleExportPdf = async (setupId, sessionName) => {
      if (!canManageSetup) return; 
      try {
          const setupRef = doc(db, 'setupSheets', setupId);
          const snap = await getDoc(setupRef); 
          if (!snap.exists() || snap.data().teamId !== teamId) {
              alert(t('setups.export_error')); 
              return;
          }
          const setup = snap.data();
          const setupData = setup.setupData || {};
          const displayDate = setup.dataSessione ? new Date(setup.dataSessione.toDate()).toLocaleDateString(getLocaleFormat()) : 'N/A';
          
          const teamRef = doc(db, 'teams', teamId);
          const teamSnap = await getDoc(teamRef);
          const teamData = teamSnap.exists() ? teamSnap.data() : {};

          let proxyLogoUrl = null;
          if (teamData?.logoUrlLight) {
              proxyLogoUrl = `https://wsrv.nl/?url=${encodeURIComponent(teamData.logoUrlLight)}&output=png`;
          }
          
          let proxyVehicleUrl = null;
          if (vehicle?.imageUrl) {
              proxyVehicleUrl = `https://wsrv.nl/?url=${encodeURIComponent(vehicle.imageUrl)}&output=png`;
          }

          // Fetch driver feedback per questo setup
          const fbQuery = query(collection(db, 'driverFeedback'), where('setupId', '==', setupId));
          const fbSnap = await getDocs(fbQuery);
          const driverFeedbacks = fbSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          driverFeedbacks.sort((a, b) => b.createdAt?.toMillis() - a.createdAt?.toMillis());

          // Fetch template config per i custom fields
          let templateConfig = [];
          if (vehicle?.templateId) {
            const templateSnap = await getDoc(doc(db, 'setupTemplates', vehicle.templateId));
            if (templateSnap.exists()) {
              templateConfig = templateSnap.data().modules || [];
            }
          }

          const blob = await pdf(
            <SetupPdf 
              setupData={setupData} 
              setup={setup}
              vehicleData={vehicle} 
              teamData={teamData} 
              teamLogoBase64={proxyLogoUrl} 
              vehicleImageBase64={proxyVehicleUrl}
              templateConfig={templateConfig}
              t={t}
              language={currentLang?.startsWith('en') ? 'en' : 'it'}
              displayDate={displayDate}
              driverFeedbacks={driverFeedbacks}
            />
          ).toBlob();
          
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${vehicle?.name || vehicle?.modello || 'Vehicle'}_Setup_${setup.eventName || 'Event'}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
      } catch (error) {
          console.error("Error exporting setup PDF:", error);
          alert("Errore durante l'esportazione in PDF. Riprova."); 
      }
  };

  // --- NUOVA FUNZIONE: REPORT STORICO VEICOLO IN PDF ---
  const handleExportVehicleHistory = async () => {
    if (!canManageSetup || !vehicle) return;
    setIsExportingHistory(true);
    try {
      // 1. Recupera ISSUES
      const issuesQuery = query(
        collection(db, "vehicleIssues"),
        where("vehicleId", "==", vehicleId),
        where("teamId", "==", teamId),
        orderBy("createdAt", "desc")
      );
      const issuesSnap = await getDocs(issuesQuery);
      const issuesData = issuesSnap.docs.map(doc => {
        const d = doc.data();
        return {
          Data: formatDateForExcel(d.createdAt),
          Categoria: d.category,
          Stato: d.status,
          Descrizione: d.description,
          Soluzione: d.resolution || '-',
          Priorita: d.priority
        };
      });

      // 2. Recupera WORK ORDERS
      const woQuery = query(
        collection(db, "workOrders"),
        where("vehicleId", "==", vehicleId),
        where("teamId", "==", teamId),
        orderBy("createdAt", "desc")
      );
      const woSnap = await getDocs(woQuery);
      const woData = woSnap.docs.map(doc => {
        const d = doc.data();
        return {
          Data: formatDateForExcel(d.createdAt),
          Titolo: d.title,
          Stato: d.status,
          Note: d.description || '-'
        };
      });

      const teamRef = doc(db, 'teams', teamId);
      const teamSnap = await getDoc(teamRef);
      const teamData = teamSnap.exists() ? teamSnap.data() : {};

      let proxyLogoUrl = null;
      if (teamData?.logoUrlLight) {
          proxyLogoUrl = `https://wsrv.nl/?url=${encodeURIComponent(teamData.logoUrlLight)}&output=png`;
      }

      const displayDate = new Date().toLocaleDateString(getLocaleFormat());

      const blob = await pdf(
        <VehicleHistoryPdf 
          vehicle={vehicle} 
          teamData={teamData} 
          teamLogoBase64={proxyLogoUrl} 
          issuesData={issuesData}
          woData={woData}
          displayDate={displayDate}
        />
      ).toBlob();
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `History_${vehicle?.marca}_${vehicle?.modello}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting history PDF:", error);
      alert("Errore durante l'esportazione del PDF.");
    } finally {
      setIsExportingHistory(false);
    }
  };
  // ----------------------------------------------

  const handleOpenEditModal = () => {
    if (!vehicle || isLoadingDrivers || isLoadingTemplates) return;
    const mainDriverObject = driversList.find(d => d.id === vehicle.mainDriverId) || null;
    const templateObject = templatesList.find(t => t.id === vehicle.templateId) || null;
    
    setEditFormData({
      marca: vehicle.marca || '',
      modello: vehicle.modello || '',
      numeroGara: vehicle.numeroGara || '',
      categoria: vehicle.categoria || 'GT',
      dryWeight: vehicle.dryWeight || '', 
      mainDriver: mainDriverObject,
      passportLink: vehicle.passportLink || '',
      template: templateObject 
    });
    setIsEditModalOpen(true);
  };
  
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };
  
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleUpdateVehicle = async (e) => {
    e.preventDefault();
    if (!editFormData.marca || !editFormData.modello || !editFormData.template) {
      alert(t('edit_modal.error_required')); 
      return;
    }
    setIsSavingVehicle(true);
    const vehicleRef = doc(db, 'vehicles', vehicleId);
    try {
      await updateDoc(vehicleRef, {
        marca: editFormData.marca,
        modello: editFormData.modello,
        numeroGara: editFormData.numeroGara,
        categoria: editFormData.categoria || 'GT',
        dryWeight: Number(editFormData.dryWeight) || null, 
        mainDriverId: editFormData.mainDriver ? editFormData.mainDriver.id : null,
        mainDriverName: editFormData.mainDriver ? editFormData.mainDriver.nomeCompleto : null,
        passportLink: editFormData.passportLink.trim(),
        templateId: editFormData.template.id,
        templateName: editFormData.template.templateName
      });
      handleCloseEditModal(); 
    } catch (error) {
      console.error("Error updating vehicle:", error);
      alert(t('edit_modal.error_update')); 
    } finally {
      setIsSavingVehicle(false);
    }
  };

  const handleToggleEventChecklistItem = async (checklistId, itemIndex) => {
    const checklist = eventChecklists.find(c => c.id === checklistId);
    if (!checklist) return;
    const updatedItems = checklist.items.map((item, i) => 
      i === itemIndex ? { ...item, checked: !item.checked } : item
    );
    const allComplete = updatedItems.every(item => item.checked);
    const checklistRef = doc(db, 'eventChecklists', checklistId);
    try {
      await updateDoc(checklistRef, {
        items: updatedItems,
        isComplete: allComplete
      });
    } catch (error) {
      console.error("Error updating event checklist item:", error);
      alert(t('checklists.update_error')); 
    }
  };


  if (isLoadingVehicle || isLoadingDrivers || isLoadingTemplates) {
    return ( <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box> );
  }

  if (!vehicle) {
    return ( <Box sx={{ textAlign: 'center', mt: 5 }}><Typography variant="h4" color="error">{t('vehicle_not_found')}</Typography><Button component={RouterLink} to="/" sx={{ mt: 2 }}>{t('back_to_garage')}</Button></Box> );
  }

  // --- RENDER ---
  return (
    <>
      {/* 1. Header "Tripla A" */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          backgroundColor: 'background.paper', 
          border: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={t('back_to_garage')}>
            <IconButton color="inherit" component={RouterLink} to={`/garage`} sx={{ mr: 2, p: 0.5 }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              {vehicle.marca} {vehicle.modello}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {t('header_nr')} {vehicle.numeroGara || 'N/A'} {vehicle.mainDriverName ? `| ${vehicle.mainDriverName}` : ''}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{mt: 0.5}}>
              {t('header_template')} {vehicle.templateName || 'N/A'}
            </Typography>
          </Box>
        </Box>
        {isManager && (
          <Box>
            
            {/* --- PULSANTE QR CODE --- */}
            <Tooltip title={t('qrcode_tooltip')}>
              <IconButton 
                color="inherit" 
                size="large" 
                onClick={() => setIsQRModalOpen(true)} 
                sx={{ mr: 1 }}
              >
                <QrCodeIcon />
              </IconButton>
            </Tooltip>
            {/* --------------------------- */}

            {/* --- BOTTONE REPORT STORICO PDF --- */}
            <Tooltip title={t('report_history.tooltip')}>
              <IconButton 
                color="error" 
                size="large" 
                onClick={handleExportVehicleHistory}
                disabled={isExportingHistory}
                sx={{ mr: 1 }}
              >
                {isExportingHistory ? <CircularProgress size={24} /> : <PictureAsPdfIcon />}
              </IconButton>
            </Tooltip>
            {/* ----------------------------------- */}

            {vehicle.passportLink && (
              <Tooltip title={t('header_passport_tooltip')}>
                <IconButton color="primary" size="large" href={vehicle.passportLink} target="_blank" rel="noopener noreferrer" sx={{ mr: 1 }} >
                  <ArticleIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('header_edit_tooltip')}>
              <IconButton onClick={handleOpenEditModal} color="inherit" size="large" sx={{ mr: 1 }} disabled={isLoadingDrivers || isLoadingTemplates}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('header_delete_tooltip')}>
              <IconButton onClick={handleDeleteVehicle} color="error" size="large">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Paper>

      {/* 2. Contenuto Tab */}
      <TabContext value={activeTab}>
        <Paper
          elevation={0} 
          sx={{ border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={handleChangeTab} aria-label="Vehicle Dashboard">
              <MuiTab label={t('tabs.setups')} value="1" />
              <MuiTab label={t('tabs.issues')} value="2" />
              <MuiTab label={t('tabs.lifing')} value="3" />
              <MuiTab 
                label={t('tabs.checklists')}
                value="4" 
                icon={eventParam ? <ListAltIcon color="warning" /> : undefined} 
                iconPosition="end"
              />
              <MuiTab label={t('tab_feedbacks')} value="5" />
            </TabList>
          </Box>
          
          <TabPanel value="1" sx={{ p: { xs: 1, sm: 2 } }}>
            {isLoadingSetups ? <CircularProgress /> : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2, p: 1 }}>
                  <Box>
                    {isManager && selectedSetups.length === 2 && (
                      <Button variant="contained" color="success" component={RouterLink} to={`/compare/${selectedSetups[0]}/with/${selectedSetups[1]}`}>
                        {t('setups.compare_button')}
                      </Button>
                    )}
                  </Box>
                  {canManageSetup && (
                    <Button variant="contained" startIcon={<AddIcon />} component={RouterLink} to={`/vehicle/${vehicleId}/setup/new`} size="large">
                      {t('setups.add_button')}
                    </Button>
                  )}
                </Box>
                
                {isManager && <Typography variant="body1" sx={{mb:1, p: 1}}>{t('setups.compare_prompt')}</Typography>}
                {!isManager && <Typography variant="body1" sx={{mb:1, p: 1, fontStyle: 'italic'}}>{t('setups.manage_prompt')}</Typography>}

                {!isLoadingSetups && setupSheets.length === 0 && (
                  <Typography sx={{mt: 2, fontStyle: 'italic', p: 1}}>{t('setups.empty')}</Typography>
                )}
                <List>
                  {setupSheets.map(sheet => (
                    <ListItem
                      key={sheet.id}
                      disablePadding
                      sx={{ backgroundColor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 1 }}
                      secondaryAction={
                        canManageSetup && (
                          <Box sx={{ display: 'flex', gap: 0.5, pr: 1 }}>
                            <Tooltip title={t('setups.export_pdf', { defaultValue: 'Esporta PDF' })}>
                                <IconButton 
                                    edge="end" 
                                    aria-label="export pdf" 
                                    onClick={(e) => { e.stopPropagation(); handleExportPdf(sheet.id, sheet.sessionName); }}
                                >
                                    <PictureAsPdfIcon color="primary" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('setups.duplicate_tooltip')}>
                                <IconButton 
                                    edge="end" 
                                    aria-label="duplicate" 
                                    onClick={(e) => { e.stopPropagation(); handleDuplicateSetup(sheet.id); }}
                                >
                                    <ContentCopyIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('setups.delete_tooltip')}>
                                <IconButton 
                                    edge="end" 
                                    aria-label="delete" 
                                    onClick={(e) => { 
                                        e.stopPropagation(); 
                                        handleDeleteSetup(sheet.id, sheet.sessionName); 
                                    }}
                                >
                                    <DeleteIcon color="error" />
                                </IconButton>
                            </Tooltip>
                          </Box>
                        )
                      }
                    >
                      {isManager && (
                        <Checkbox
                          edge="start" checked={selectedSetups.includes(sheet.id)}
                          disabled={selectedSetups.length === 2 && !selectedSetups.includes(sheet.id)}
                          onClick={(e) => handleSelectSetup(e, sheet.id)}
                          onChange={() => {}} sx={{ ml: 1, color: '#90caf9' }}
                        />
                      )}
                      <ListItemButton
                        component={RouterLink}
                        to={`/vehicle/${vehicleId}/setup/${sheet.id}`}
                        sx={{ ml: isManager ? 0 : 2, borderRadius: 2 }}
                      >
                        <ListItemText
                          primary={`${sheet.eventName || 'Event'} - ${sheet.sessionName || 'Session'}`}
                          secondary={ `${t('setups.sheet_date')} ${sheet.dataSessione ? new Date(sheet.dataSessione.toDate()).toLocaleDateString(getLocaleFormat()) : 'N/A'} | ${t('setups.sheet_driver')} ${sheet.nomePilota || 'N/A'}` }
                          primaryTypographyProps={{ fontWeight: 'medium' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </TabPanel>
          
          <TabPanel value="2" sx={{ p: 0 }}>
            <IssueLog vehicleId={vehicleId} teamId={teamId} />
          </TabPanel>
          
          <TabPanel value="3" sx={{ p: 0 }}>
             <ComponentLifing vehicleId={vehicleId} teamId={teamId} userRole={userRole} />
          </TabPanel>
          
          <TabPanel value="4" sx={{ p: { xs: 1, sm: 3 } }}>
            <Typography variant="h5" gutterBottom>
              {t('checklists.title')}
            </Typography>

            {isLoadingEventChecklists || isLoadingFutureEvents ? (
              <CircularProgress sx={{ display: 'block', margin: 'auto' }} />
            ) : (
              <>
                {(() => {
                  const eventMap = new Map(allFutureEvents.map(e => [e.id, e]));
                  const groups = new Map();
                  for (const checklist of eventChecklists) {
                    if (eventMap.has(checklist.eventId)) { 
                      if (!groups.has(checklist.eventId)) {
                        groups.set(checklist.eventId, []);
                      }
                      groups.get(checklist.eventId).push(checklist);
                    }
                  }
                  const groupedChecklists = Array.from(groups.entries())
                    .map(([eventId, checklists]) => ({
                      event: eventMap.get(eventId),
                      checklists: checklists,
                    }))
                    .sort((a, b) => a.event.startDate.seconds - b.event.startDate.seconds);

                  if (groupedChecklists.length === 0) {
                     return (
                        <Alert severity="success" sx={{ m: 2 }}>
                          {t('checklists.empty')}
                        </Alert>
                     );
                  }

                  return groupedChecklists.map(({ event, checklists }) => (
                    <Box 
                      key={event.id}
                      ref={(el) => (eventSectionRefs.current[event.id] = el)}
                      sx={{ mb: 3 }}
                    >
                      <Typography variant="h6" gutterBottom>
                        {event.eventName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('checklists.event_starts')} {format(event.startDate.toDate(), 'dd/MM/yyyy')}
                      </Typography>
                      
                      {checklists.map((checklist) => (
                        <Paper 
                          key={checklist.id}
                          elevation={0}
                          sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.default' }}
                        >
                          <Typography variant="h6" gutterBottom>
                            {checklist.templateName}
                          </Typography>
                          <Divider sx={{ mb: 1 }} />
                          <List dense>
                            {checklist.items.map((item, index) => (
                              <ListItem key={index} disablePadding>
                                <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
                                  <Checkbox 
                                    edge="start" 
                                    checked={item.checked} 
                                    onChange={() => handleToggleEventChecklistItem(checklist.id, index)} 
                                    tabIndex={-1} 
                                    disableRipple 
                                  />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={item.text} 
                                  sx={{ 
                                    textDecoration: item.checked ? 'line-through' : 'none', 
                                    color: item.checked ? 'text.secondary' : 'text.primary' 
                                  }} 
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      ))}
                    </Box>
                  ));
                })()}
              </>
            )}
          </TabPanel>

          <TabPanel value="5" sx={{ p: { xs: 1, sm: 2 } }}>
            <Box sx={{ p: 1 }}>
              <Typography variant="h6" gutterBottom>Feedback Inseriti dai Piloti</Typography>
              {isLoadingFeedbacks ? <CircularProgress /> : (
                feedbacks.length === 0 ? <Typography sx={{ fontStyle: 'italic' }}>Nessun feedback presente.</Typography> : (
                  feedbacks.map(fb => (
                    <Paper key={fb.id} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">{fb.driverName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fb.createdAt?.toDate().toLocaleString()}
                        </Typography>
                      </Box>
                      {fb.corners && fb.corners.map((c, i) => (
                        <Box key={i} sx={{ mb: 2, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                          <Typography variant="body2" fontWeight="bold">{c.name}</Typography>
                          <Typography variant="body2">Ingresso: {c.entry}/10 | Centro: {c.mid}/10 | Uscita: {c.exit}/10</Typography>
                          {c.notes && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                              <MicIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />"{c.notes}"
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Paper>
                  ))
                )
              )}
            </Box>
          </TabPanel>
        </Paper> 
      </TabContext>

      {/* 3. Modale Modifica Veicolo (Invariata) */}
      <Dialog open={isEditModalOpen} onClose={handleCloseEditModal} maxWidth="sm" fullWidth>
        <DialogTitle>{t('edit_modal.title')}</DialogTitle>
        <Box component="form" onSubmit={handleUpdateVehicle}>
          <DialogContent>
            <TextField
              name="marca"
              label={t('edit_modal.make')}
              value={editFormData.marca}
              onChange={handleEditFormChange}
              required
              fullWidth
              margin="normal"
              disabled={isSavingVehicle}
            />
            <TextField
              name="modello"
              label={t('edit_modal.model')}
              value={editFormData.modello}
              onChange={handleEditFormChange}
              required
              fullWidth
              margin="normal"
              disabled={isSavingVehicle}
            />
            <Autocomplete
              options={templatesList}
              getOptionLabel={(option) => option.templateName || ''}
              value={editFormData.template}
              disabled={isLoadingTemplates || isSavingVehicle}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(event, newValue) => {
                setEditFormData(prev => ({ ...prev, template: newValue }));
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label={t('edit_modal.template')}
                  required
                  margin="normal"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingTemplates ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ flex: 1 }}
            />
            
            {/* Campo Categoria e dryWeight */}
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  name="numeroGara"
                  label={t('edit_modal.race_nr')}
                  value={editFormData.numeroGara}
                  onChange={handleEditFormChange}
                  fullWidth
                  margin="normal"
                  disabled={isSavingVehicle}
                  sx={{ flex: 1 }}
                />
                <FormControl fullWidth margin="normal" sx={{ flex: 1 }}>
                  <InputLabel>Categoria</InputLabel>
                  <Select
                    name="categoria"
                    value={editFormData.categoria || 'GT'}
                    label="Categoria"
                    onChange={handleEditFormChange}
                    disabled={isSavingVehicle}
                  >
                    <MenuItem value="GT">GT</MenuItem>
                    <MenuItem value="Formula">Formula</MenuItem>
                    <MenuItem value="Legend">Legend</MenuItem>
                    <MenuItem value="Nascar">Nascar</MenuItem>
                    <MenuItem value="V8-Super">V8-Super</MenuItem>
                    <MenuItem value="Kart">Kart</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  name="dryWeight"
                  label={t('edit_modal.dry_weight')}
                  type="number"
                  inputProps={{ step: "0.1", min: "0" }}
                  value={editFormData.dryWeight}
                  onChange={handleEditFormChange}
                  fullWidth
                  margin="normal"
                  disabled={isSavingVehicle}
                  InputProps={{
                      endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                  }}
                  sx={{ flex: 1 }}
                />
            </Box>
            
            <Autocomplete
              options={driversList}
              getOptionLabel={(option) => option.nomeCompleto || ''}
              value={editFormData.mainDriver}
              disabled={isLoadingDrivers || isSavingVehicle}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(event, newValue) => {
                setEditFormData(prev => ({ ...prev, mainDriver: newValue }));
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label={t('edit_modal.driver')}
                  margin="normal"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingDrivers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ flex: 1 }}
            />
            <TextField
              name="passportLink"
              label={t('edit_modal.passport')}
              value={editFormData.passportLink}
              onChange={handleEditFormChange}
              fullWidth
              margin="normal"
              disabled={isSavingVehicle}
              type="url"
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={handleCloseEditModal} disabled={isSavingVehicle}>
              {t('edit_modal.cancel_button')}
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={isSavingVehicle || isLoadingDrivers || isLoadingTemplates || !editFormData.marca || !editFormData.modello || !editFormData.template}
            >
              {isSavingVehicle ? <CircularProgress size={24} /> : t('edit_modal.save_button')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* 4. MODALE QR CODE (Invariata) */}
      <Dialog open={isQRModalOpen} onClose={() => setIsQRModalOpen(false)}>
        <DialogTitle>{t('qrcode_modal_title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
          <QRCode value={window.location.href} size={200} />
          <Typography variant="caption" sx={{ mt: 2 }}>
            {t('qrcode_instructions')}
          </Typography>
          <Typography variant="h6" sx={{ mt: 1, fontWeight: 'bold' }}>
            {vehicle.marca} {vehicle.modello} #{vehicle.numeroGara}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => window.print()}>{t('qrcode_print')}</Button>
          <Button onClick={() => setIsQRModalOpen(false)}>{t('qrcode_close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default VehicleDashboard;